import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";

const IraabActivity = ({
  currentActivity,
  submitted,
  iraabAnswers,
  selectedIraabWord,
  setSelectedIraabWord,
  setIraabAnswers,
  preview, // ⭐ passed from ActivityRenderer
}) => {
  // ⭐ In preview mode use separate state so the student state doesn't change
  const [previewWordIndex, setPreviewWordIndex] = useState(null);
  if (!currentActivity?.Iraab?.Value) return null;
  const harakaatOptions = ["َ", "ُ", "ِ", "ْ"];
  const isSubmitted = submitted?.isSubmitted || false;

  // ---------------- WORD PROCESSING ----------------
  const allWords = currentActivity.Iraab.Value.split(" ").map((word) => {
    const harakaPattern = /[ًٌٍَُِْ]$/;
    const last = word.slice(-1);
    const hasHaraka = harakaPattern.test(last);
    const cleaned = hasHaraka ? word.slice(0, -1) : word;

    return { original: word, base: cleaned, hasHaraka };
  });

  // ---------------- CHECK CORRECTNESS ----------------
  let isCorrect = true;
  allWords.forEach((w, idx) => {
    if (!w.hasHaraka) return;
    const userWord = iraabAnswers[idx] || "";
    const correct = w.original;
    if (userWord.trim() !== correct.trim()) isCorrect = false;
  });

  const resultBgColor = isSubmitted
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "transparent";

  // ---------------- CLICK HANDLER ----------------
  const handleWordClick = (index, hasHaraka) => {
    if (!hasHaraka) return;

    if (preview) {
      // ⭐ In preview → show popup only, no selection saving
      setPreviewWordIndex(index);
      return;
    }

    // ⭐ Student mode
    if (isSubmitted) return;
    setSelectedIraabWord(selectedIraabWord === index ? null : index);
  };

  // ---------------- STUDENT SELECTION ONLY ----------------
  const handleIraabSelection = (haraka) => {
    if (preview) return; // ⭐ block selection in preview

    if (selectedIraabWord !== null) {
      setIraabAnswers((prev) => ({
        ...prev,
        [selectedIraabWord]: allWords[selectedIraabWord].base + haraka,
      }));
    }
    setSelectedIraabWord(null);
  };

  // ---------------- POPUP SELECTION INDEX ----------------
  const popupIndex = preview ? previewWordIndex : selectedIraabWord;

  return (
    <>
      {/* ---------------- Instructions ---------------- */}
      <Typography
        variant="body2"
        sx={{
          m: 1.2,
          p: "4px 8px",
          fontStyle: "italic",
          backgroundColor: "#dfc4df",
          display: "inline-block",
          borderRadius: "4px",
        }}
      >
        Select the correct إعراب by choosing the correct haraka.
      </Typography>

      {/* ---------------- WORDS ---------------- */}
      <Box sx={{ direction: "rtl", textAlign: "right", mt: 1 }}>
        {allWords.map((w, index) => (
          <Box
            key={index}
            display="inline-block"
            mx={0.5}
            onClick={() => handleWordClick(index, w.hasHaraka)}
            sx={{
              cursor: w.hasHaraka ? "pointer" : "default",
              opacity: w.hasHaraka ? 1 : 0.6,
            }}
          >
            <Typography
              sx={{
                backgroundColor: "#fff",
                fontFamily: "Traditional Arabic, serif",
                fontSize: "1.6rem",
                px: 1,
                borderRadius: "6px",
                border:
                  popupIndex === index ? "2px solid #2196f3" : "1px solid #ddd",
              }}
            >
              {iraabAnswers[index] || w.base}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ---------------- POPUP ---------------- */}
      {popupIndex !== null && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          bgcolor="rgba(0,0,0,0.4)"
          zIndex={1400}
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() =>
            preview ? setPreviewWordIndex(null) : setSelectedIraabWord(null)
          }
        >
          <Box
            bgcolor="#fff"
            borderRadius={2}
            p={2}
            boxShadow={5}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack spacing={1}>
              {harakaatOptions.map((haraka, idx) => (
                <Typography
                  key={idx}
                  onClick={() => handleIraabSelection(haraka)}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: 1,
                    cursor: preview ? "default" : "pointer",
                    fontSize: "1.4rem",
                    fontFamily: "Traditional Arabic, serif",
                    textAlign: "center",
                  }}
                >
                  {allWords[popupIndex].base + haraka}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      {/* ---------------- RESULT BOX ---------------- */}
      {isSubmitted && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: resultBgColor,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {isCorrect ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
            ) : (
              <CancelIcon sx={{ fontSize: 40, color: "red" }} />
            )}
          </Box>

          <Typography
            sx={{
              textAlign: "center",
              mt: 1,
              color: isCorrect ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {isCorrect ? "Correct!" : "Incorrect!"}
          </Typography>

          {/* Rationale */}
          <Box sx={{ mt: 2 }}>
            {currentActivity.Iraab?.Rationale?.Text ? (
              <Typography sx={{ background: "#fff", p: 1, borderRadius: 1 }}>
                {currentActivity.Iraab.Rationale.Text}
              </Typography>
            ) : currentActivity.Iraab?.Rationale?.ContentId ? (
              <Typography sx={{ background: "#fff", p: 1, borderRadius: 1 }}>
                Rationale ID: {currentActivity.Iraab.Rationale.ContentId}
              </Typography>
            ) : (
              <Typography fontStyle="italic">
                No rationale available.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default IraabActivity;
